import {
  FlatType,
  FloorType,
  State,
  StreetSuffix,
  StreetType,
} from '@energy-retailer/constants';
import { z } from 'zod';
import { FullPropertyAddressSchema } from '../full-property-address-schema';
import { houseNumberParser } from '../utils/address-parser';

const flatTypeSchema = z.nativeEnum(FlatType).optional();
const floorTypeSchema = z.nativeEnum(FloorType).optional();
const streetSuffixSchema = z
  .custom<keyof typeof StreetSuffix>()
  .transform((value) => StreetSuffix[value])
  .pipe(z.nativeEnum(StreetSuffix))
  .optional();
const streetTypeSchema = z.nativeEnum(StreetType);

export const addressFormatSchema = z
  .object({
    result: z.object({
      global_address_key: z.string(),
      address: z.object({
        address_line_1: z.string(),
        address_line_2: z.string(),
        address_line_3: z.string(),
        locality: z.string(),
        region: z.nativeEnum(State),
        postal_code: z.string(),
        country: z.string(),
      }),
      components: z.object({
        sub_building: z
          .object({
            floor: z
              .object({
                type: z.string().transform((value) => value.toUpperCase()),
                value: z.string(),
              })
              .partial()
              .optional(),
            door: z
              .object({
                type: z.string().transform((value) => value.toUpperCase()),
                value: z.string(),
              })
              .partial()
              .optional(),
          })
          .optional(),
        building: z.object({
          building_number: z.string(),
        }),
        street: z.object({
          name: z.string(),
          type: z.string().transform((value) => value.toUpperCase()),
          suffix: z
            .string()
            .transform((value) => value.toUpperCase())
            .optional(),
        }),
      }),
    }),
    metadata: z.object({
      address_info: z.object({
        identifier: z.object({
          dpid: z.string(),
        }),
      }),
    }),
  })
  .transform((data): FullPropertyAddressSchema => {
    const safeFormat = (value: string | undefined) => (value ? value : '');
    const safeFormatNumber = (value: number | undefined) => {
      if (value === undefined) return '';
      if (Number.isNaN(value)) return '';

      return value.toString();
    };

    const flatNumber = data.result.components.sub_building?.door?.value;
    const baseHouseNumber = data.result.components.building.building_number;

    const { houseNumber, houseSuffix, houseNumberTo, houseNumberToSuffix } =
      houseNumberParser(baseHouseNumber);

    const safeFormatHouseNumber = safeFormatNumber(houseNumber);
    const safeFormatHouseSuffix = safeFormat(houseSuffix);
    const safeFormatHouseNumberTo = safeFormatNumber(houseNumberTo);
    const safeFormatHouseNumberToSuffix = safeFormat(houseNumberToSuffix);

    const houseNumberFormatted =
      `${safeFormatHouseNumber}${safeFormatHouseSuffix} ${safeFormatHouseNumberTo}${safeFormatHouseNumberToSuffix}`.trim();

    const streetName = data.result.components.street.name;
    const streetType = streetTypeSchema.parse(
      data.result.components.street.type
    );
    const streetSuffix = streetSuffixSchema.parse(
      data.result.components.street.suffix
    );
    const suburb = data.result.address.locality;
    const postcode = data.result.address.postal_code;
    const state = data.result.address.region;

    const StructuredAddress = [
      flatNumber,
      houseNumberFormatted,
      streetName,
      streetType,
      streetSuffix,
    ].join(' ');

    const full = [StructuredAddress, suburb, postcode, state]
      .join(' ')
      .replace(/[ ]+/g, ' ')
      .trim();

    return {
      full,
      id: data.result.global_address_key,
      state,
      country: data.result.address.country,
      postcode,
      suburb,
      dpid: data.metadata.address_info.identifier.dpid,
      flatNumber,
      flatType: flatTypeSchema.parse(
        data.result.components.sub_building?.door?.type
      ),
      floorNumber: data.result.components.sub_building?.floor?.value,
      floorType: floorTypeSchema.parse(
        data.result.components.sub_building?.floor?.type
      ),
      houseNumber,
      houseSuffix,
      houseNumberTo,
      houseNumberToSuffix,
      streetName,
      streetType,
      streetSuffix,
    };
  });

export type AddressFormatSchema = z.infer<typeof addressFormatSchema>;

export const addressHeroSchema = z.object({
  address: z
    .object({
      name: z.string(),
      id: z.string(),
    })
    .optional(),
});

export type AddressHeroSchema = z.infer<typeof addressHeroSchema>;
